import {ApiError, apiFetch} from "./client";
import {formatApiError} from "./admin/errors";

/** Группы событий обоих кабинетов; свой набор и порядок у кабинета — в его модуле API. */
export type NotificationGroupKey = "order_created" | "requisites" | "success" | "cancel" | "dispute" | "balance";

export const NOTIFICATION_GROUP_TITLES: Record<NotificationGroupKey, string> = {
    order_created: "Новая заявка",
    requisites: "Реквизиты выданы",
    success: "Заявка выполнена",
    cancel: "Отмена и истечение срока",
    dispute: "Спор",
    balance: "Баланс",
};

export interface CabinetNotification {
    id: number;
    group: NotificationGroupKey;
    title: string;
    body: string;
    orderId: string | null;
    /** Куда ведёт уведомление внутри кабинета: у трейдера нет своей страницы заявки. */
    href?: string;
    occurredAt: string;
}

export interface CabinetNotificationsPage {
    items: CabinetNotification[];
    unreadCount: number;
    nextBeforeId: number | null;
}

export type TelegramChatType = "private" | "group" | "supergroup";
export interface TelegramChat {
    id: number;
    type: TelegramChatType;
    displayName: string;
    state: "connected" | "paused";
    pauseReason: string | null;
    connectedAt: string;
}
export interface TelegramLink {
    startUrl: string;
    groupUrl: string;
    expiresAt: string;
}
export interface TelegramSettings {
    available: boolean;
    botUsername: string;
    link: TelegramLink | null;
    chats: TelegramChat[];
}
export interface TelegramTestResult {
    results: {id: number; sent: boolean; error?: string}[];
}

export interface NotificationGroupInfo {
    key: NotificationGroupKey;
    title: string;
}

export interface NotificationChannelSelection {
    cabinet: NotificationGroupKey[];
    telegram: NotificationGroupKey[];
}

export interface CabinetNotificationSettings {
    cabinet: NotificationGroupKey[];
    telegramGroups: NotificationGroupKey[];
    groups: NotificationGroupInfo[];
    telegram: TelegramSettings;
}

/** Коды отказов этого раздела — строго как в контракте API. */
export const NOTIFICATION_ERROR_CODES = [
    "notification_group_invalid", "telegram_unavailable", "telegram_not_connected",
] as const;
export type NotificationErrorCode = (typeof NOTIFICATION_ERROR_CODES)[number];
const NOTIFICATION_ERROR_TEXTS: Record<NotificationErrorCode, string> = {
    notification_group_invalid: "Такой группы уведомлений нет. Обновите страницу и повторите.",
    telegram_unavailable: "Telegram сейчас недоступен. Повторите позже.",
    telegram_not_connected: "Пока нет подключённых чатов. Откройте ссылку и нажмите «Старт» в боте.",
};

/** Код отказа из тела ответа: только из списка раздела, иначе null. */
export function notificationErrorCode(err: unknown): NotificationErrorCode | null {
    if (!(err instanceof ApiError) || !err.body) return null;
    try {
        const parsed: unknown = JSON.parse(err.body);
        const code = parsed && typeof parsed === "object" ? (parsed as {code?: unknown}).code : undefined;
        return NOTIFICATION_ERROR_CODES.find((known) => known === code) ?? null;
    } catch {
        return null;
    }
}

/** Русский текст для любой ошибки раздела. */
export function notificationErrorMessage(err: unknown): string {
    const code = notificationErrorCode(err);
    if (code) return NOTIFICATION_ERROR_TEXTS[code];
    if (err instanceof ApiError) {
        if (err.status === 429) return "Слишком много запросов. Повторите позже.";
        if (err.status >= 500) return "Сервис временно недоступен. Повторите через пару минут.";
        return formatApiError(err);
    }
    return "Не удалось связаться с сервером. Проверьте соединение и повторите.";
}

function jsonRequest(method: string, payload: unknown): RequestInit {
    return {method, body: JSON.stringify(payload)};
}

export interface CabinetNotificationsApi {
    list: (
        token: string | null,
        query?: {beforeId?: number; limit?: number},
        signal?: AbortSignal,
    ) => Promise<CabinetNotificationsPage>;
    markSeen: (token: string | null, lastId: number) => Promise<{unreadCount: number}>;
    getSettings: (token: string | null) => Promise<CabinetNotificationSettings>;
    saveSettings: (token: string | null, selection: NotificationChannelSelection) => Promise<CabinetNotificationSettings>;
    createLink: (token: string | null) => Promise<TelegramLink & {botUsername: string}>;
    revokeLink: (token: string | null) => Promise<CabinetNotificationSettings>;
    testTelegram: (token: string | null) => Promise<TelegramTestResult>;
    disconnectChat: (token: string | null, id: number) => Promise<CabinetNotificationSettings>;
}

/** Запросы раздела «Уведомления»: у кабинетов один контракт, отличается только адрес. */
export function cabinetNotificationsApi(base: string): CabinetNotificationsApi {
    return {
        // Лента, новые сверху; beforeId листает к более старым.
        list: (token, query = {}, signal) => {
            const params = new URLSearchParams();
            if (query.beforeId !== undefined) params.set("beforeId", String(query.beforeId));
            if (query.limit !== undefined) params.set("limit", String(query.limit));
            const suffix = params.toString();
            return apiFetch<CabinetNotificationsPage>(suffix ? `${base}?${suffix}` : base, token, {signal});
        },
        // Прочитано до id включительно.
        markSeen: (token, lastId) => apiFetch(`${base}/seen`, token, jsonRequest("POST", {lastId})),
        getSettings: (token) => apiFetch(`${base}/settings`, token),
        saveSettings: (token, selection) => apiFetch(`${base}/settings`, token, jsonRequest("PUT", selection)),
        createLink: (token) => apiFetch(`${base}/telegram/link`, token, {method: "POST"}),
        revokeLink: (token) => apiFetch(`${base}/telegram/link`, token, {method: "DELETE"}),
        testTelegram: (token) => apiFetch(`${base}/telegram/test`, token, {method: "POST"}),
        disconnectChat: (token, id) => apiFetch(`${base}/telegram/chats/${id}`, token, {method: "DELETE"}),
    };
}
